// Thin wrapper around `navigator.mediaDevices.getUserMedia` +
// `MediaRecorder` for the chat composer's voice-recording flow.
//
// The composer (`ChatRoomComposerRich`) drives the UI state machine
// (recording timer + cancel/send buttons) and emits `onStartVoice` /
// `onSendVoiceMessage` / `onCancelVoiceMessage` callbacks. The web
// host instantiates `WebVoiceRecorder` and routes those callbacks
// through `start()` / `stop()` / `cancel()`:
//
//   onStartVoice  → recorder.start()
//   onSendVoice   → blob = recorder.stop(); sendVoiceMessage(blob)
//   onCancelVoice → recorder.cancel()
//
// `onProgress(cb)` lets the host observe `MediaRecorder`'s 100-ms
// chunk callbacks if it wants a finer-grained meter than the
// composer's `Date.now()` ticker — the composer's existing timer is
// sufficient for the recording-bar UI so most hosts ignore it.

// Codec choice priority — ordered most-to-least preferred for
// cross-platform interoperability:
//
//   1. `audio/mp4;codecs=mp4a.40.2` — AAC-LC in MP4. Decoded natively
//      by both AVFoundation (desktop's `PingAudioPeaks.swift` →
//      `AVAudioFile`) and Web Audio (`AudioContext.decodeAudioData`).
//      Supported by ~85% of browsers (Safari, Chrome, Edge).
//   2. `audio/mp4` — generic MP4 container; the browser picks the
//      codec (usually AAC). Same decode path on both surfaces.
//   3. `audio/webm;codecs=opus` — Firefox-only fallback. Web→web
//      playback works (AudioContext decodes Opus), but received-on-
//      desktop loses real waveforms because AVAudioFile cannot decode
//      `audio/webm;codecs=opus`. Acceptable corner.
//   4. `audio/webm` — last-resort container.
const CANDIDATE_MIME_TYPES: readonly string[] = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
];

/**
 * Picks the first MIME type supported by `MediaRecorder` on this
 * browser. Returns the empty string when none match — the caller is
 * expected to surface a "voice not supported" message in that case.
 */
function pickSupportedMimeType(): string {
  const MR = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (MR === undefined || typeof MR.isTypeSupported !== 'function') {
    return '';
  }
  for (const mime of CANDIDATE_MIME_TYPES) {
    if (MR.isTypeSupported(mime)) return mime;
  }
  return '';
}

export interface WebVoiceRecorderProgress {
  readonly chunkBytes: number;
  readonly elapsedMs: number;
}

// ── Minimal structural types for the Web Speech API. The DOM lib types
// for SpeechRecognition are not universally present (and `webkit`-
// prefixed on Chrome/Safari), so we declare just what we use.
interface SpeechRecognitionAlternativeLike {
  readonly transcript: string;
}
interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionEventLike {
  readonly resultIndex: number;
  readonly results: {
    readonly length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | undefined {
  const w = globalThis as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

export class WebVoiceRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private mimeType = '';
  private startedAtMs = 0;
  private readonly progressListeners = new Set<(p: WebVoiceRecorderProgress) => void>();
  // Live-amplitude pipeline. The AnalyserNode hangs off a
  // dedicated `AudioContext` (we don't reuse the playback driver's
  // singleton because the recording context's `sampleRate` must
  // match the mic, not the default output rate). `rafHandle` drives
  // the meter at ~30 Hz via `requestAnimationFrame`.
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private rafHandle: number | null = null;
  private readonly amplitudeListeners = new Set<(amp: number) => void>();
  // Record-time speech-to-text. Best-effort: when the browser exposes
  // the Web Speech API we transcribe the mic live and accumulate the
  // final results. Absent (Firefox) → `transcript` stays empty and the
  // bubble simply hides the transcript affordance.
  private speech: SpeechRecognitionLike | null = null;
  private transcript = '';
  // Resolves when the SpeechRecognition session has delivered its final
  // results (`onend`). `stop()` awaits this so `currentTranscript` is
  // complete before the caller reads it — Chrome/Safari fire the last
  // `onresult` AFTER `stop()` is requested, so reading synchronously
  // would otherwise drop the tail of the transcript.
  private speechEndPromise: Promise<void> | null = null;
  private resolveSpeechEnd: (() => void) | null = null;

  /**
   * Requests microphone permission and starts capturing audio.
   * Resolves once `MediaRecorder.state === 'recording'`. Throws when
   * permission is denied, the browser has no `MediaRecorder`, or no
   * preferred MIME type is supported.
   */
  async start(): Promise<void> {
    if (this.recorder !== null) {
      // Idempotent — restart from scratch.
      this.cancel();
    }
    if (typeof navigator === 'undefined' || navigator.mediaDevices === undefined) {
      throw new Error('Microphone is not available in this browser.');
    }
    const MR = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
    if (MR === undefined) {
      throw new Error('MediaRecorder is not supported in this browser.');
    }
    const mime = pickSupportedMimeType();
    if (mime === '') {
      throw new Error('No supported audio MIME type available.');
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MR(stream, { mimeType: mime });
    this.stream = stream;
    this.recorder = recorder;
    this.mimeType = mime;
    this.chunks = [];
    this.startedAtMs = Date.now();

    recorder.ondataavailable = (event: BlobEvent): void => {
      if (event.data.size > 0) {
        this.chunks.push(event.data);
        if (this.progressListeners.size > 0) {
          const elapsedMs = Date.now() - this.startedAtMs;
          for (const cb of this.progressListeners) {
            try {
              cb({ chunkBytes: event.data.size, elapsedMs });
            } catch {
              // Ignore listener errors — progress callbacks are
              // purely informational.
            }
          }
        }
      }
    };

    // 100ms chunks — fine-grained enough for a 30 Hz progress meter
    // without flooding the main thread on long recordings.
    recorder.start(100);

    // Best-effort record-time transcription via the Web Speech API.
    // Runs in parallel with MediaRecorder off the same mic permission.
    this.transcript = '';
    this.speechEndPromise = null;
    this.resolveSpeechEnd = null;
    try {
      const SR = getSpeechRecognitionCtor();
      if (SR !== undefined) {
        const rec = new SR();
        rec.lang =
          (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
        rec.continuous = true;
        rec.interimResults = false;
        rec.onresult = (event: SpeechRecognitionEventLike): void => {
          let finalText = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result?.isFinal && result.length > 0) {
              finalText += result[0]?.transcript ?? '';
            }
          }
          if (finalText.length > 0) {
            this.transcript = `${this.transcript} ${finalText}`.trim();
          }
        };
        rec.onerror = (): void => {
          // Best-effort — a recognition error just leaves the accumulated
          // transcript as-is; recording itself is unaffected.
          this.resolveSpeechEnd?.();
        };
        // `onend` fires after the recognizer flushes its FINAL results,
        // so anything queued lands in `this.transcript` before we
        // resolve. `stop()` awaits this promise.
        rec.onend = (): void => {
          this.resolveSpeechEnd?.();
        };
        this.speechEndPromise = new Promise<void>((resolve) => {
          this.resolveSpeechEnd = resolve;
        });
        rec.start();
        this.speech = rec;
      }
    } catch {
      // Best-effort — recording works without transcription.
      this.speech = null;
      this.speechEndPromise = null;
    }

    // Hook up the AnalyserNode pipeline so subscribers can stream the
    // live amplitude meter for the composer waveform. Failure to wire
    // the analyser is non-fatal: recording itself still works, the UI
    // simply receives no amplitude updates and falls back to silence.
    try {
      const AC = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
      if (AC !== undefined && this.amplitudeListeners.size > 0) {
        const ctx = new AC();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.65;
        source.connect(analyser);
        this.audioCtx = ctx;
        this.sourceNode = source;
        this.analyser = analyser;
        this.startMeterLoop();
      }
    } catch {
      // Best-effort. If the AudioContext fails we keep recording.
    }
  }

  private startMeterLoop(): void {
    const analyser = this.analyser;
    if (analyser === null) return;
    const buffer = new Uint8Array(analyser.fftSize);
    let lastEmitMs = 0;
    const TARGET_FRAME_MS = 33; // ~30 Hz
    const loop = (): void => {
      this.rafHandle =
        (
          globalThis as {
            requestAnimationFrame?: (cb: FrameRequestCallback) => number;
          }
        ).requestAnimationFrame?.(loop) ?? null;
      const now = Date.now();
      if (now - lastEmitMs < TARGET_FRAME_MS) return;
      lastEmitMs = now;
      analyser.getByteTimeDomainData(buffer);
      // Compute RMS amplitude. The byte-time-domain samples are
      // centered around 128 (0..255 range); subtract 128 and divide
      // by 128 to normalize to -1..1, then RMS to a 0..1 scalar.
      let sum = 0;
      for (let i = 0; i < buffer.length; i++) {
        const v = (buffer[i]! - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buffer.length);
      // Boost a little so soft speech reads as visible bars without
      // looking like clipping. 2.5× lands typical speech around
      // 0.4–0.7, peaks near 1.0.
      const amplitude = Math.min(1, rms * 2.5);
      for (const cb of this.amplitudeListeners) {
        try {
          cb(amplitude);
        } catch {
          // Best-effort.
        }
      }
    };
    loop();
  }

  /**
   * Subscribe to live RMS amplitude samples (0..1) at ~30 Hz while
   * recording. Returns an unsubscribe function. Amplitudes start
   * flowing once `start()` resolves and the AnalyserNode is wired
   * (which only happens when at least one subscriber is registered
   * at start-time, so subscribe BEFORE calling `start()`).
   */
  onAmplitude(callback: (amplitude: number) => void): () => void {
    this.amplitudeListeners.add(callback);
    return () => {
      this.amplitudeListeners.delete(callback);
    };
  }

  /**
   * Stops the recorder and resolves with the concatenated `Blob`.
   * The stream's underlying tracks are released. Returns an empty
   * blob if `start()` was never called.
   */
  stop(): Promise<Blob> {
    const recorder = this.recorder;
    if (recorder === null) {
      return Promise.resolve(new Blob([], { type: 'audio/mp4' }));
    }
    return new Promise<Blob>((resolve, reject) => {
      const finalize = (): void => {
        const blob = new Blob(this.chunks, { type: this.mimeType });
        this.releaseStream();
        this.recorder = null;
        // Wait for the speech recognizer to flush its FINAL results
        // (Chrome/Safari deliver the last `onresult` after `stop()`),
        // so `currentTranscript` is complete when the caller reads it.
        void this.finalizeSpeech(1500).then(() => resolve(blob));
      };
      recorder.onstop = finalize;
      try {
        if (recorder.state === 'inactive') {
          finalize();
        } else {
          recorder.stop();
        }
      } catch (err) {
        this.releaseStream();
        this.recorder = null;
        this.abortSpeech();
        reject(err as Error);
      }
    });
  }

  /**
   * Discards in-flight audio and releases the microphone. Safe to
   * call repeatedly.
   */
  cancel(): void {
    const recorder = this.recorder;
    if (recorder !== null) {
      try {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        // Best-effort — releasing the stream is the important part.
      }
    }
    this.abortSpeech();
    this.releaseStream();
    this.recorder = null;
    this.chunks = [];
  }

  /**
   * Subscribes to per-chunk progress events. Returns an
   * unsubscribe function. Mostly useful for tests / future
   * waveform meters; the composer's UI ticker is independent.
   */
  onProgress(callback: (progress: WebVoiceRecorderProgress) => void): () => void {
    this.progressListeners.add(callback);
    return () => {
      this.progressListeners.delete(callback);
    };
  }

  /** The negotiated MIME type. Empty string until `start()` resolves. */
  get currentMimeType(): string {
    return this.mimeType;
  }

  /**
   * The accumulated record-time transcript (Web Speech). Empty string
   * when the browser has no Speech API or nothing was recognised. Read
   * this AFTER `stop()` resolves to attach it to the sent envelope.
   */
  get currentTranscript(): string {
    return this.transcript;
  }

  /**
   * Gracefully stops speech recognition and resolves once it has
   * delivered its FINAL results (`onend`) or `timeoutMs` elapses —
   * whichever comes first. Keeps `onresult` wired throughout so the
   * trailing transcript lands in `this.transcript`.
   */
  private async finalizeSpeech(timeoutMs: number): Promise<void> {
    const rec = this.speech;
    const endPromise = this.speechEndPromise;
    this.speech = null;
    this.speechEndPromise = null;
    if (rec === null) {
      this.resolveSpeechEnd = null;
      return;
    }
    try {
      rec.stop();
    } catch {
      // Best-effort — fall through to the wait/timeout.
    }
    if (endPromise === null) {
      this.resolveSpeechEnd = null;
      return;
    }
    await Promise.race([
      endPromise,
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
    this.resolveSpeechEnd = null;
  }

  /** Immediately abort speech recognition (cancel path — no waiting). */
  private abortSpeech(): void {
    const rec = this.speech;
    this.speech = null;
    this.speechEndPromise = null;
    this.resolveSpeechEnd = null;
    if (rec !== null) {
      try {
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        rec.abort();
      } catch {
        // Best-effort.
      }
    }
  }

  private releaseStream(): void {
    // NOTE: speech recognition is finalized/aborted separately (see
    // `finalizeSpeech` / `abortSpeech`) so its trailing transcript isn't
    // dropped when the mic stream is released.
    if (this.rafHandle !== null) {
      try {
        (
          globalThis as {
            cancelAnimationFrame?: (handle: number) => void;
          }
        ).cancelAnimationFrame?.(this.rafHandle);
      } catch {
        // Best-effort.
      }
      this.rafHandle = null;
    }
    if (this.sourceNode !== null) {
      try {
        this.sourceNode.disconnect();
      } catch {
        // Best-effort.
      }
      this.sourceNode = null;
    }
    if (this.analyser !== null) {
      try {
        this.analyser.disconnect();
      } catch {
        // Best-effort.
      }
      this.analyser = null;
    }
    if (this.audioCtx !== null) {
      try {
        void this.audioCtx.close();
      } catch {
        // Best-effort.
      }
      this.audioCtx = null;
    }
    if (this.stream !== null) {
      for (const track of this.stream.getTracks()) {
        try {
          track.stop();
        } catch {
          // Best-effort.
        }
      }
      this.stream = null;
    }
  }
}
